import React, { useCallback, useMemo, useState } from "react";

import cx from "classnames";
import { NodeType } from "prosemirror-model";
import { EditorView } from "prosemirror-view";
import { Loader } from "semantic-ui-react";
import { v4 } from "uuid";

import AssigneeFilter, { type AssigneeType } from "~/common/AssigneeFilter";
import { getContext } from "~/lib/prose/plugins/context";
import { decodeGlobalId } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import type { NodesFromEdges } from "~/lib/utils";
import CycleListInput from "~/okrs/components/CycleListInput";
import CreateObjective from "~/okrs/CreateObjective";
import type { ObjectiveAttributes } from "~/okrs/Objective";
import StreamSettingsModal from "~/stream/StreamSettings";
import { Button } from "~/ui";
import { ButtonLink, HelpCenterIcon, Modal } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";
import toast, { ToastActionButton } from "~/ui/toast";

import SelectKeyResultDropdown from "./SelectKeyResultDropdown";

import type { SelectKeyResultDropdownQuery$data } from "~/__generated__/SelectKeyResultDropdownQuery.graphql";

import styles from "~/lib/prose/plugins/objectives/SelectKeyResult.module.css";

export type KeyResultType = NodesFromEdges<SelectKeyResultDropdownQuery$data["keyResults"]>;
export type KeyResultsEdgesType = readonly ({ node: KeyResultType } | null)[];

export interface GroupedKeyResultsOption {
	readonly label: string;
	readonly options: readonly KeyResultType[];
}

interface SelectKeyResultProps {
	view: EditorView;
	close: () => void;
	preSelectedKeyResults?: KeyResultType[];
}

export default function SelectKeyResult({ view, close, preSelectedKeyResults }: SelectKeyResultProps) {
	const { state, dispatch } = view;
	const context = getContext(view.state);
	const streamId = context?.streamId;
	const [selectedAssignee, setSelectedAssignee] = useState<AssigneeType[]>([]);
	const [cycles, setCycles] = useState<string[]>([]);
	const [selectedKeyResults, setSelectedKeyResults] = useState<KeyResultType[]>(preSelectedKeyResults || []);

	const openStreamSettings = useCallback(() => {
		__globalModalContext?.open(({ close }) => (
			<StreamSettingsModal close={close} streamId={streamId} defaultSection="objectives" />
		));
	}, [streamId]);

	const onInsert = useCallback(() => {
		const { tr } = state;
		selectedKeyResults.forEach((keyResult, index) => {
			const insertPos = state.doc.resolve(state.selection.from).end(1) + 1 + index;

			const blockId = v4();
			const keyResultId = keyResult.id;

			const newNode = (state.schema.nodes.key_result as NodeType).createAndFill({
				blockId,
				keyResultId,
			});

			if (!newNode) {
				toast.error("Sorry, something went wrong while embedding key result(s).");
				return;
			}

			tr.insert(insertPos, newNode);

			dispatchAnalyticsTrack("Key Result Embedded", {
				/* eslint-disable camelcase */
				key_result_id: keyResultId,
				block_id: blockId,
				stream_id: streamId ? decodeGlobalId(streamId)[1] : undefined,
				note_id: context.noteId ? decodeGlobalId(context.noteId)[1] : undefined,
				/* eslint-enable camelcase */
			});
		});

		dispatch(tr);
		if (streamId) {
			toast.success(
				"Key result(s) inserted. Enable the objectives tab from series settings for quick access to any objective.",
				{
					actions: <ToastActionButton onClick={openStreamSettings}>Open series settings</ToastActionButton>,
					closeButton: true,
				},
			);
		}
		close();
	}, [close, dispatch, openStreamSettings, selectedKeyResults, state, streamId, context.noteId]);

	const handleAssigneesChange = useCallback(
		(selectedAssignee: AssigneeType[]) => {
			if (!selectedAssignee || selectedAssignee.length === 0) {
				setSelectedAssignee([]);
			} else {
				setSelectedAssignee(selectedAssignee);
			}
		},
		[setSelectedAssignee],
	);

	const handleCycleChange = useCallback(
		(selectedCycles: string[]) => {
			setCycles(selectedCycles);
		},
		[setCycles],
	);

	const handleKeyResultsChange = useCallback(
		(selectedKeyResults: KeyResultType[]) => {
			setSelectedKeyResults(selectedKeyResults);
		},
		[setSelectedKeyResults],
	);

	const reOpenSelectKeyResult = useCallback(
		(preSelectedKeyResults: KeyResultType[]) => {
			__globalModalContext?.open(({ close }) => (
				<SelectKeyResult view={view} close={close} preSelectedKeyResults={preSelectedKeyResults} />
			));
		},
		[view],
	);

	const onObjectiveCreateClosed = useCallback(
		(close: () => void, preSelectedKeyResults: KeyResultType[]) => {
			close();
			reOpenSelectKeyResult(preSelectedKeyResults);
		},
		[reOpenSelectKeyResult],
	);

	const openCreateObjective = useCallback(() => {
		close();
		__globalModalContext?.open(({ close }) => {
			const onClose = (objective: ObjectiveAttributes) => {
				let createdKeyResults: KeyResultType[] = [];
				if (objective) {
					createdKeyResults = objective.keyResults.map(keyResult => {
						return {
							id: keyResult.id,
							title: keyResult.title,
							objective: {
								title: objective.title,
							},
						};
					});
				}
				onObjectiveCreateClosed(close, selectedKeyResults.concat(createdKeyResults));
			};
			// eslint-disable-next-line react/jsx-no-bind
			return <CreateObjective onClose={onClose} hideSuccessToast />;
		});
	}, [close, selectedKeyResults, onObjectiveCreateClosed]);

	const assigneeId = useMemo(() => {
		return selectedAssignee.map(a => a.user?.id || a.team?.id || "");
	}, [selectedAssignee]);

	return (
		<Modal onClose={close} className={styles.modal}>
			<Modal.Header className={styles.header}>
				<Modal.Title>Insert key result</Modal.Title>
				<Modal.HeaderActions onClose={close}>
					<HelpCenterIcon articleId="5607359" title="How to create a new objective" />
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body className={styles.body}>
				<p>
					Insert an existing key result to your note, or{" "}
					<ButtonLink onClick={openCreateObjective}>create an objective</ButtonLink> to get started.
				</p>
				<div className={cx(styles.filters)}>
					<AssigneeFilter
						values={selectedAssignee}
						setValues={handleAssigneesChange}
						className={styles.filterDropdown}
						isMulti={false}
					/>
					<React.Suspense fallback={<Loader active inline />}>
						<CycleListInput isMulti onChange={handleCycleChange} className={styles.filterDropdown} selected={cycles} />
					</React.Suspense>
				</div>
				<SelectKeyResultDropdown
					values={selectedKeyResults}
					onChange={handleKeyResultsChange}
					assignedTo={assigneeId}
					cycles={cycles}
				/>
			</Modal.Body>
			<Modal.Footer className={styles.footer}>
				<Button size="normal" onClick={close}>
					Cancel
				</Button>
				<Button size="normal" variant="primary" onClick={onInsert} disabled={selectedKeyResults.length === 0}>
					Insert
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
